/**
 * Read-only snapshot of the Timecard approval rulesets, transcribed from the BPM
 * Worklist Task Configuration screenshots (TimecardApprovalRules,
 * ProjectTimecardApprovalELARules1, ProjectTimecardApprovalRules).
 *
 * AMX IF/THEN rules are sealed on this SaaS pod, so this is a manually captured
 * snapshot for viewing + as copy-to-editor templates — not a live read.
 *
 * Each rule:
 *   - name        rule name as shown in BPM
 *   - then[]      ordered THEN participant types (palette kinds) for the chips
 *   - ifText      plain-English IF condition (free form)
 *   - thenDetail  specifics of the THEN (list builder, hierarchy type, levels, auto action)
 *   - autoApprove true when the THEN has Auto Action = "APPROVE"
 */

import { APPROVER_PALETTE } from './absenceApprovalRulesSnapshot';

export { APPROVER_PALETTE };

const MH = 'managementHierarchy';
const USERS = 'users';
const SAA = 'selfAutoApprove';

/**
 * Primary Timecard Approval task ruleset (worker / manager timecards).
 * From the "TimecardApprovalRules" ruleset screenshots.
 */
export const TIMECARD_APPROVAL_RULES_SNAPSHOT = Object.freeze({
  source: 'snapshot',
  capturedAt: '2026-05-31',
  task: {
    name: 'Timecard Approval',
    code: 'TimecardApprovalTask',
    taskKey: 'timecard-approval',
    category: 'time_labor',
  },
  note:
    'Read-only snapshot transcribed from BPM Worklist Task Configuration (TimecardApprovalRules). AMX rules are not readable via API on this pod, so values are captured from the tenant UI and may need re-verification.',
  rules: [
    {
      name: 'ManualEntryRule',
      then: [MH],
      ifText: 'attributeChar33 isn\'t null AND attributeChar33.trim() isn\'t "ORA_HWM_WORKER_ENTRY"',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
    {
      name: 'AutoApprovalRule',
      then: [MH],
      ifText: 'attributeChar33 isn\'t null AND attributeChar33.trim() is "ORA_HWM_WORKER_ENTRY"',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
    {
      name: 'TimeCard_Assign_Category',
      then: [MH, SAA],
      autoApprove: true,
      ifText:
        'RequestorHRInformation.result.employmentCategory.trim() in { "XX_ADMIN_PROJ", "XX_C_P", "XX_C_NP" }',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getPrincipal(Task.payload.process.Requester, -1, "", "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getPrincipal(Task.payload.process.Requester, -1, "", "LINE_MANAGER")',
        autoActionEnabled: true,
        autoAction: '"APPROVE"',
      },
    },
    {
      name: 'TimeCard',
      then: [MH],
      ifText:
        'NOT( employmentCategory.trim() in { "XX_ADMIN_PROJ", "XX_C_P", "XX_C_NP" } )',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
    {
      name: 'DeviceEntry',
      then: [USERS],
      ifText: 'attributeChar33 is "Web Clock"',
      thenRefs: ['shanthan.polagani@drivestream.com, saisruthi.murthy@drivestream.com'],
      thenSpec: {
        listBuilder: 'Resource',
        responseType: 'Required',
        startingParticipant: 'Users: shanthan.polagani@drivestream.com, saisruthi.murthy@drivestream.com',
      },
    },
    {
      name: 'LineManagerRule',
      then: [MH, SAA],
      autoApprove: true,
      ifText: 'attributeChar4 isn\'t null AND attributeChar4.trim() isn\'t "ORA_HWM_WORKER_ENTRY"',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getPrincipal(Task.payload.process.Requester, -1, "", "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getPrincipal(Task.payload.process.Requester, -1, "", "LINE_MANAGER")',
        autoActionEnabled: true,
        autoAction: '"APPROVE"',
      },
    },
    {
      name: 'EmployeeRule',
      then: [MH],
      ifText: 'attributeChar4 isn\'t null AND attributeChar4.trim() is "ORA_HWM_WORKER_ENTRY"',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
  ],
});

/**
 * Project Timecard ruleset (ELA) — ProjectTimecardApprovalELARules1 (1-3 of 3).
 */
export const PROJECT_TIMECARD_ELA_RULES_SNAPSHOT = Object.freeze({
  source: 'snapshot',
  capturedAt: '2026-05-31',
  task: {
    name: 'Project Timecard Approval (ELA)',
    code: 'ProjectTimecardApprovalTask',
    taskKey: 'project-timecard-approval-ela',
    category: 'time_labor',
  },
  note:
    'Read-only snapshot transcribed from ProjectTimecardApprovalELARules1. The AutoApprovalRule uses IgnoreParticipant for one variant and a Supervisory auto-approve for another.',
  rules: [
    {
      name: 'AutoApprovalRule',
      then: [SAA],
      autoApprove: true,
      ifText: 'attributeChar10.trim() is "I"  (variant: attributeChar17 is "I" → call IgnoreParticipant)',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getPrincipal(Task.creator, -1, "", null)',
        topParticipant: 'HierarchyBuilder.getPrincipal(Task.creator, -1, "", null)',
        autoActionEnabled: true,
        autoAction: '"APPROVE"  (variant: call IgnoreParticipant("AutoApprovalRule", Lists))',
      },
    },
    {
      name: 'LineManagerRule',
      then: [MH],
      ifText: 'projectManager is Task.payload.payloadRequester AND attributeChar17 is "C"',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getManager("supervisory", ApprovalTimeRecordGroup.projectManager, -1, null, "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getManager("supervisory", ApprovalTimeRecordGroup.projectManager, -1, null, "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
    {
      name: 'ProjectManagerRule',
      then: [MH],
      ifText: 'attributeChar17 is "C" AND projectManager isn\'t Task.payload.payloadRequester',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getPrincipal(ApprovalTimeRecordGroup.projectManager, -1, "", "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getPrincipal(ApprovalTimeRecordGroup.projectManager, -1, "", "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
  ],
});

/**
 * Project Timecard ruleset — ProjectTimecardApprovalRules (1-2 of 2).
 */
export const PROJECT_TIMECARD_RULES_SNAPSHOT = Object.freeze({
  source: 'snapshot',
  capturedAt: '2026-05-31',
  task: {
    name: 'Project Timecard Approval',
    code: 'ProjectTimecardApprovalRulesTask',
    taskKey: 'project-timecard-approval',
    category: 'time_labor',
  },
  note: 'Read-only snapshot transcribed from ProjectTimecardApprovalRules.',
  rules: [
    {
      name: 'ProjectManagerRule',
      then: [MH],
      ifText: 'attributeChar17 is "C" AND projectManager isn\'t Task.payload.payloadRequester',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getPrincipal(ApprovalTimeRecordGroup.projectManager, -1, "", "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getPrincipal(ApprovalTimeRecordGroup.projectManager, -1, "", "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
    {
      name: 'LineManagerRule',
      then: [MH],
      ifText: 'projectManager is null AND attributeNumber1 is null',
      thenSpec: {
        listBuilder: 'Supervisory',
        responseType: 'Required',
        levels: 1,
        startingParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        topParticipant: 'HierarchyBuilder.getManager("supervisory", Task.payload.process.Requester, -1, null, "LINE_MANAGER")',
        autoActionEnabled: false,
        autoAction: 'null',
      },
    },
  ],
});

/** All timecard rulesets, rendered as stacked read-only panels. */
export const TIMECARD_RULESETS = Object.freeze([
  TIMECARD_APPROVAL_RULES_SNAPSHOT,
  PROJECT_TIMECARD_ELA_RULES_SNAPSHOT,
  PROJECT_TIMECARD_RULES_SNAPSHOT,
]);

export default TIMECARD_APPROVAL_RULES_SNAPSHOT;
